"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader } from "lucide-react";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-accent/30 p-8 text-center bg-black/40 backdrop-blur-sm rounded-lg">
        <p className="font-sans text-lg text-accent mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-2 border-accent bg-transparent text-accent hover:bg-accent hover:text-black font-sans uppercase tracking-widest font-bold transition-all duration-300">
          Sign Up Again
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full space-y-8">
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="FULL NAME" className="h-12 border-0 border-b border-white/20 bg-transparent text-white placeholder:text-white/40 placeholder:font-sans placeholder:uppercase placeholder:tracking-widest focus-visible:border-accent focus-visible:ring-0 px-0 rounded-lg transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-sans text-xs text-red-400 mt-1" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input type="email" placeholder="EMAIL ADDRESS" className="h-12 border-0 border-b border-white/20 bg-transparent text-white placeholder:text-white/40 placeholder:font-sans placeholder:uppercase placeholder:tracking-widest focus-visible:border-accent focus-visible:ring-0 px-0 rounded-lg transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-sans text-xs text-red-400 mt-1" />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input type="tel" placeholder="PHONE NUMBER (OPTIONAL)" className="h-12 border-0 border-b border-white/20 bg-transparent text-white placeholder:text-white/40 placeholder:font-sans placeholder:uppercase placeholder:tracking-widest focus-visible:border-accent focus-visible:ring-0 px-0 rounded-lg transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-sans text-xs text-red-400 mt-1" />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormControl>
                <textarea placeholder="STYLE PREFERENCES (OPTIONAL)" rows={2} className="w-full border-0 border-b border-white/20 bg-transparent text-white placeholder:text-white/40 placeholder:font-sans placeholder:uppercase placeholder:tracking-widest focus-visible:outline-none focus-visible:border-accent p-0 py-2 font-sans text-base resize-none rounded-lg transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-sans text-xs text-red-400 mt-1" />
            </FormItem>} />
      </div>

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg border-2 border-accent bg-transparent text-accent hover:bg-accent hover:text-black font-sans uppercase tracking-widest font-bold text-base transition-all duration-300 shadow-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Join the Atelier"}
        </Button>
      </div>

      {submitError && <div className="border border-red-500/50 p-4 bg-red-500/10 rounded-lg">
          <p className="font-sans text-xs text-red-400">
            {submitError}
          </p>
        </div>}

      <p className="font-sans uppercase tracking-widest text-[10px] text-white/40 text-center">
        Privacy guaranteed. Exclusive access only.
      </p>
    </Form>;
}
export default function NewsletterSignup() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1779" id="newslettersignup" className="relative w-full bg-black text-white overflow-hidden transition-colors duration-500">
      {}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
      backgroundImage: `radial-gradient(circle at 2px 2px, hsl(45 68% 47%) 1px, transparent 0)`,
      backgroundSize: '40px 40px'
    }} aria-hidden="true" />

      <div ref={ref} className={`relative py-24 px-6 md:px-12 lg:px-24 transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1) ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {}
            <div className="lg:col-span-8 space-y-8">
              <div className="flex items-center gap-4">
                <span aria-hidden="true" className="inline-block w-12 h-[1px] bg-accent" />
                <span className="font-sans text-xs md:text-sm uppercase tracking-[0.3em] font-medium text-accent">
                  The Inner Circle
                </span>
              </div>

              <h2 className="heading font-bold uppercase leading-tight tracking-tight text-white" style={{
              fontSize: "clamp(2rem, 5vw, 2.5rem)"
            }}>
                Join the <span className="text-accent">Atelier</span>
              </h2>

              <p className="body text-white/60 max-w-2xl text-base md:text-lg leading-relaxed">
                Step into the world of Lush Fashion. Receive curated seasonal lookbooks, 
                early access to limited drops, and invitations to private atelier events. 
                Your journey into gold-standard luxury begins here.
              </p>

              <div className="hidden lg:block pt-12">
                <div className="grid grid-cols-3 gap-8 border-t border-white/10 pt-8">
                  {[{
                  label: "Exclusive",
                  value: "Collections"
                }, {
                  label: "Priority",
                  value: "Shipping"
                }, {
                  label: "Private",
                  value: "Sales"
                }].map((item, i) => <div key={i} data-index={i} className="space-y-1">
                      <p className="text-[10px] uppercase tracking-widest text-accent">{item.label}</p>
                      <p className="text-sm font-medium text-white">{item.value}</p>
                    </div>)}
                </div>
              </div>
            </div>

            {}
            <div className="lg:col-span-4 bg-white/5 backdrop-blur-md p-8 md:p-10 rounded-lg border border-white/10 shadow-2xl">
              <FormProvider formKey="insider_signup_home" formId="insider_signup_home" sectionName="newslettersignup" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email_address: {
                required: true,
                pattern: "email"
              }
            }} submitText="Join the Atelier" successMessage="Welcome to the Atelier. Your invitation is on its way." errorMessage="Something went wrong. Please try again.">
                <NewsletterForm />
              </FormProvider>
            </div>

          </div>
        </div>
      </div>
    </section>;
}